import { Badge } from '@buyerflo/ui/components/Badge'
import { Card } from '@buyerflo/ui/components/Card'
import { useQuery } from '@tanstack/react-query'
import Loading from 'components/Loading'
import { fmtAud, fmtDate, referralStatusTone } from 'affiliate/shared'
import { useState } from 'react'
import { SizableText, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

const STATUSES = ['trialing', 'active', 'past_due', 'cancelled'] as const

export default function Referrals() {
    const trpc = useTRPC()
    const query = useQuery(trpc.affiliate.getMyReferrals.queryOptions())
    const [filter, setFilter] = useState<Set<string>>(new Set())

    if (query.isLoading) return <Loading />
    const referrals = query.data ?? []
    const filtered = filter.size ? referrals.filter(r => filter.has(r.status)) : referrals

    const toggle = (s: string) =>
        setFilter(prev => {
            const next = new Set(prev)
            next.has(s) ? next.delete(s) : next.add(s)
            return next
        })

    return (
        <YStack gap="$4">
            <XStack gap="$2" flexWrap="wrap">
                {STATUSES.map(s => (
                    <View
                        key={s}
                        cursor="pointer"
                        opacity={filter.size === 0 || filter.has(s) ? 1 : 0.4}
                        onPress={() => toggle(s)}
                    >
                        <Badge tone={referralStatusTone[s]}>{s.replace('_', ' ')}</Badge>
                    </View>
                ))}
            </XStack>

            <Card inset>
                <XStack
                    paddingHorizontal="$4"
                    paddingVertical="$2.5"
                    borderBottomWidth={1}
                    borderBottomColor="$borderColor"
                    gap="$3"
                >
                    <SizableText flex={2} size="$1" color="$colorMuted">Referral</SizableText>
                    <SizableText flex={1.2} size="$1" color="$colorMuted">Status</SizableText>
                    <SizableText flex={1.2} size="$1" color="$colorMuted" display="none" $sm={{ display: 'flex' }}>Joined</SizableText>
                    <SizableText flex={1.2} size="$1" color="$colorMuted">Earned</SizableText>
                </XStack>

                {filtered.length === 0 && (
                    <View padding="$4">
                        <SizableText color="$colorMuted">No referrals yet — share your link to get started.</SizableText>
                    </View>
                )}

                {filtered.map(r => (
                    <XStack
                        key={r.referralId}
                        paddingHorizontal="$4"
                        paddingVertical="$3"
                        gap="$3"
                        alignItems="center"
                        borderBottomWidth={1}
                        borderBottomColor="$borderColor"
                    >
                        <SizableText flex={2} fontWeight="600">{r.label}</SizableText>
                        <View flex={1.2}>
                            <Badge tone={referralStatusTone[r.status] ?? 'neutral'}>{r.status.replace('_', ' ')}</Badge>
                        </View>
                        <SizableText flex={1.2} size="$2" color="$colorMuted" display="none" $sm={{ display: 'flex' }}>{fmtDate(r.signedUpAt)}</SizableText>
                        <SizableText flex={1.2} fontWeight="600">{fmtAud(r.earnedCents)}</SizableText>
                    </XStack>
                ))}
            </Card>
        </YStack>
    )
}
